// Purpose.js
import React from 'react';
import {
  Box, Typography, Card, CardContent, Paper, Grid2
} from '@mui/material';
// Grid2: use the Unstable_Grid2 (or stable Grid2 in MUI v6) import form
import {
  School, Business, AdminPanelSettings, Group, BugReport, MultipleStopRounded
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import DashboardSolutionCard from './DashboardSolutionCard';

const MotionBox = motion(Box);
const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, when: 'beforeChildren' }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } }
};

const hoverLift = {
  whileHover: { y: -2 },
  whileTap: { scale: 0.995 },
  transition: { type: 'tween', duration: 0.15 }
};

const Purpose = () => {
  return (
    <MotionBox
      component="section"
      initial="hidden"
      animate="show"
      variants={containerVariants}
      sx={{ minHeight: '100vh', my: 8 }}
    >
      <MotionBox variants={itemVariants}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 1 }}>
          Simplifying Educational Service Delivery for Third-Party Organizations
        </Typography>

        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 6 }}>
          Understand who benefits and how our platform transforms administrative oversight.
        </Typography>
      </MotionBox>

      <Grid2 container spacing={4} alignItems="center" justifyContent="center">
        {/* Step 1: Third-Party Organization */}
        <Grid2 size={{ xs: 12, md: 3 }}>
          <MotionCard
            variants={itemVariants}
            {...hoverLift}
            raised
            sx={{
              height: '100%',
              border: '1px dashed #90caf9',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              backgroundColor: '#e3f2fd'
            }}
          >
            <CardContent>
              <Business color="primary" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" component="div" gutterBottom>
                Third-Party Organization
              </Typography>
              <Typography variant="body2" color="text.secondary">
                (e.g., Your Organization)
                <br />
                Delivering educational services.
              </Typography>
            </CardContent>
          </MotionCard>
        </Grid2>

        {/* Arrow / Connection */}
        <Grid2 size={{ xs: 12, md: 1 }}>
          <MotionBox variants={itemVariants}>
            <Typography textAlign="center" variant="h4" sx={{ color: '#666' }}>
              ➔
            </Typography>
          </MotionBox>
        </Grid2>

        {/* Step 2: Educational Service Delivery */}
        <Grid2 size={{ xs: 12, md: 3 }}>
          <MotionCard
            variants={itemVariants}
            {...hoverLift}
            raised
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              backgroundColor: '#e8f5e9',
              border: '2px solid #4caf50'
            }}
          >
            <CardContent>
              <School color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" component="div" gutterBottom>
                Educational Service
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Provided to schools (e.g., LAUSD).
                <br />
                Requires careful coordination.
              </Typography>
            </CardContent>
          </MotionCard>
        </Grid2>

        {/* Arrow / Connection */}
        <Grid2 size={{ xs: 12, md: 1 }}>
          <MotionBox variants={itemVariants}>
            <Typography textAlign="center" variant="h4" sx={{ color: '#666' }}>
              ➔
            </Typography>
          </MotionBox>
        </Grid2>

        {/* Step 3: Administrative Oversight (The Pain Point) */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <MotionPaper
            variants={itemVariants}
            {...hoverLift}
            elevation={3}
            sx={{ p: 3, backgroundColor: '#ffebee', border: '2px solid #ef5350' }}
          >
            <BugReport color="error" sx={{ fontSize: 70, mb: 2 }} />
            <Typography variant="h6" component="div" gutterBottom sx={{ color: '#ef5350' }}>
              The Administrative Headache
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Managing complex data from various sources:
            </Typography>
            <Box sx={{ mt: 2, '& > div': { mb: 1 } }}>
              <Typography variant="body2">• Tutor schedules & performance</Typography>
              <Typography variant="body2">• Student progress & attendance</Typography>
              <Typography variant="body2">• Assessment results</Typography>
              <Typography variant="body2">• Program material usage</Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: '#ef5350' }}>
              Fragmented data leads to manual reporting, delayed insights, and operational inefficiencies.
            </Typography>
          </MotionPaper>
        </Grid2>
      </Grid2>

      {/* Break for Solution */}
      <MotionBox variants={itemVariants} sx={{ my: 8 }}>
        <Typography variant="h3" component="div" sx={{ mb: 0 }}>
          Our Solution: A Unified Platform
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Transforming Chaos into Clarity.
        </Typography>
      </MotionBox>

      {/* Solution Section */}
      <Grid2 container spacing={4} alignItems="stretch" justifyContent="center">
        {/* Administrative */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <MotionCard
            variants={itemVariants}
            {...hoverLift}
            raised
            sx={{
              height: '100%',
              border: '1px dashed #90caf9',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              backgroundColor: '#bbdefb'
            }}
          >
            <CardContent>
              <AdminPanelSettings color="primary" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" component="div" gutterBottom>
                For Administrators
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                Our platform centralizes all tutor and student data, providing comprehensive oversight and eliminating manual data collection. Gain immediate insights into program effectiveness and resource allocation.
              </Typography>
            </CardContent>
          </MotionCard>
        </Grid2>

        {/* Connect */}
        <Grid2 size={{ xs: 12, md: 1 }}>
          <MotionBox variants={itemVariants}>
            <Typography textAlign="center" variant="h4" sx={{ color: '#666', mt: 5 }}>
              <MultipleStopRounded sx={{ fontSize: 40 }} />
            </Typography>
          </MotionBox>
        </Grid2>

        {/* Tutors/Students */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <MotionCard
            variants={itemVariants}
            {...hoverLift}
            raised
            sx={{
              height: '100%',
              display: 'flex',
              border: '1px dashed #c8e6c9',
              flexDirection: 'column',
              justifyContent: 'center',
              backgroundColor: '#c8e6c9'
            }}
          >
            <CardContent>
              <Group color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" component="div" gutterBottom>
                For Tutors/Teachers & Students
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                Streamlined session management, digital assessments, and access to all materials directly support boots-on-the-ground educational delivery, ensuring a smoother experience for everyone.
              </Typography>
            </CardContent>
          </MotionCard>
        </Grid2>

        <Grid2 size={{ xs: 12 }}>
          <MotionBox variants={itemVariants}>
            <Typography textAlign="center" variant="h4" sx={{ color: '#666' }}>
              &#8593;
            </Typography>
          </MotionBox>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          {/* If DashboardSolutionCard is a Card/Paper, you can wrap it too */}
          <MotionBox variants={itemVariants} {...hoverLift}>
            <DashboardSolutionCard />
          </MotionBox>
        </Grid2>
      </Grid2>
    </MotionBox>
  );
};

export default Purpose;